import { useMemo } from 'react';

import {
  useClonalityAnalysisExecutions,
  useClonalityComparisonsForExecutions,
  useDatasets,
  useSamplesByIds
} from '../services/api/KeynomeAPIHooks';
import {
  getClonalityComparisonValue,
  isSuccessfulComputedClonalityComparison
} from './clonalityComparisonUtils';
import { symmetricKey } from './ClonalityHeatmap';

const byCompletedAtDesc = (left, right) => {
  const leftCompletedAt = left.attributes.completed_at || '';
  const rightCompletedAt = right.attributes.completed_at || '';

  if (rightCompletedAt !== leftCompletedAt) {
    return rightCompletedAt.localeCompare(leftCompletedAt);
  }

  return left.attributes.organism.localeCompare(right.attributes.organism);
};

export const useClonalityMatrix = ({ organizationId, activeProjectId, organismName }) => {
  const { data: executions, isLoading: isExecutionsLoading } = useClonalityAnalysisExecutions(organizationId);

  const eligibleExecutions = useMemo(() => (
    (executions || []).filter((execution) => (
      execution.attributes.is_active && execution.attributes.status === 'succeeded'
    ))
  ), [executions]);

  const activeExecutions = useMemo(() => (
    eligibleExecutions
      .filter((execution) => execution.attributes.organism === organismName)
      .sort(byCompletedAtDesc)
  ), [eligibleExecutions, organismName]);

  const activeExecutionIds = useMemo(
    () => activeExecutions.map((execution) => Number(execution.id)),
    [activeExecutions]
  );
  const comparisonResults = useClonalityComparisonsForExecutions(activeExecutionIds);

  const unionedComparisons = useMemo(() => (
    comparisonResults.flatMap((result) => result.data || [])
  ), [comparisonResults]);

  const distinctLibSeqIds = useMemo(() => {
    const ids = new Set();

    unionedComparisons.forEach((comparison) => {
      const leftLibSeqId = Number(comparison.attributes.left_lib_seq_id);
      const rightLibSeqId = Number(comparison.attributes.right_lib_seq_id);
      if (!Number.isNaN(leftLibSeqId)) {
        ids.add(leftLibSeqId);
      }
      if (!Number.isNaN(rightLibSeqId)) {
        ids.add(rightLibSeqId);
      }
    });

    return [...ids];
  }, [unionedComparisons]);

  const sampleResults = useSamplesByIds(distinctLibSeqIds);
  const { data: datasets } = useDatasets(activeProjectId);

  const datasetNameById = useMemo(() => {
    const byId = new Map();
    (datasets || []).forEach((dataset) => {
      const runId = Number(dataset.id);
      if (Number.isNaN(runId)) {
        return;
      }
      byId.set(runId, dataset.attributes.name || '');
    });
    return byId;
  }, [datasets]);

  const libSeqById = useMemo(() => {
    const byId = new Map();
    const rawSampleResults = sampleResults.results || [];

    rawSampleResults.forEach((result) => {
      if (!result.data) {
        return;
      }

      const sampleId = Number(result.data.id);
      if (Number.isNaN(sampleId)) {
        return;
      }

      byId.set(sampleId, {
        project_id: result.data.attributes.project_id,
        sample_name: result.data.attributes.sample_name,
        sequencing_run_id: result.data.attributes.sequencing_run_id,
        created_at: result.data.attributes.created_at
      });
    });

    return byId;
  }, [sampleResults.results]);

  const matrixData = useMemo(() => {
    const comparisonsByPair = new Map();
    const inScopeLibSeqIds = new Set();

    unionedComparisons.forEach((comparison) => {
      const leftLibSeqId = Number(comparison.attributes.left_lib_seq_id);
      const rightLibSeqId = Number(comparison.attributes.right_lib_seq_id);

      const leftMeta = libSeqById.get(leftLibSeqId);
      const rightMeta = libSeqById.get(rightLibSeqId);
      if (!leftMeta || !rightMeta) {
        return;
      }

      if (Number(leftMeta.project_id) !== Number(activeProjectId)) {
        return;
      }
      if (Number(rightMeta.project_id) !== Number(activeProjectId)) {
        return;
      }

      if (isSuccessfulComputedClonalityComparison(comparison)) {
        inScopeLibSeqIds.add(leftLibSeqId);
        inScopeLibSeqIds.add(rightLibSeqId);
      }

      const key = symmetricKey(leftLibSeqId, rightLibSeqId);
      const value = getClonalityComparisonValue(comparison);
      const skipReason = comparison.attributes.skip_reason || null;
      const entry = { value, skipReason };

      if (
        process.env.NODE_ENV !== 'production'
        && comparisonsByPair.has(key)
        && comparisonsByPair.get(key).value !== entry.value
      ) {
        // Backend invariant violation: pair-key should be unique across active executions.
        // eslint-disable-next-line no-console
        console.warn('Clonality: duplicate pair-key across active executions for', organismName, key);
      }

      comparisonsByPair.set(key, entry);
    });

    const samples = [...inScopeLibSeqIds]
      .map((id) => {
        const sample = libSeqById.get(id);
        const runId = Number(sample?.sequencing_run_id);
        return {
          id,
          name: sample?.sample_name || String(id),
          datasetName: Number.isNaN(runId) ? '' : (datasetNameById.get(runId) || ''),
          createdAt: sample?.created_at || null
        };
      })
      .sort((left, right) => left.name.localeCompare(right.name));

    return {
      samples,
      comparisons: comparisonsByPair
    };
  }, [activeProjectId, datasetNameById, libSeqById, organismName, unionedComparisons]);

  const isComparisonsLoading = comparisonResults.some((result) => result.isLoading);

  return {
    samples: matrixData.samples,
    comparisons: matrixData.comparisons,
    activeExecutionIds,
    isLoading: isExecutionsLoading || isComparisonsLoading || sampleResults.isLoading,
    isEmpty: matrixData.samples.length === 0
  };
};
